import { AuthProvider, LoginRequest, PlayerResponse } from "./dto/auth.dto.js";
import type { PlayerDto } from "./dto/player.dto.js";
import { GlobalSettings } from "../global_settings.js";
import { login } from "./requests/auth.requests.js";
import { AuthSessionStore } from "./auth_session.js";
import { ensureDeviceId } from "../utils/device_id.js";
import { TelegramAPISettings } from "../telegram_api_settings.js";
import { OfferService } from "../offers/offer_service.js";
import { syncUserSourceAfterLogin } from "./user_source_sync.js";
import { syncPlayerAfterLogin, syncProfileFromServer } from "./player_sync.js";
import { OfflineJournal } from "../offline/offline_journal.js";
import { OfflineEconomy } from "../offline/offline_economy.js";
import { OfflineSyncService } from "../offline/offline_sync_service.js";
import { getPlayer } from "./requests/player.requests.js";
import { BotGiftService } from "../bot_gift/bot_gift_service.js";

async function resolveProvider(): Promise<LoginRequest>
{
  const device_id = await ensureDeviceId();
  const start_param = TelegramAPISettings.getStartParam() ?? undefined;

  const telegram_id = TelegramAPISettings.getUserId();
  if ( telegram_id )
  {
    const init_data = TelegramAPISettings.getInitData() ?? undefined;
    return { provider: AuthProvider.Telegram, provider_id: telegram_id, device_id, init_data, start_param };
  }

  return { provider: AuthProvider.Guest, provider_id: device_id, device_id, start_param };
}

export async function performLogin(): Promise<boolean>
{
  try
  {
    const request = await resolveProvider();
    let player: PlayerResponse = await login( request );
    AuthSessionStore.setFromPlayer( player );
    BotGiftService.setPending( player.bot_gift );
    if ( OfflineJournal.hasPending() && !OfflineSyncService.isReplaying() )
    {
      const stats = await OfflineSyncService.replayPending();
      console.info( `[Login] offline journal replayed: ${stats.replayed} ok, ${stats.dropped} dropped${stats.stopped ? ", stopped" : ""}` );
      if ( stats.replayed > 0 )
        player = { ...( await getPlayer() ), token: player.token } as PlayerResponse;
    }
    if ( OfflineJournal.hasPending() )
    {
      console.warn( '[Login] offline journal still pending — keeping local progress until the next sync' );
      syncProfileFromServer( player as unknown as PlayerDto );
      OfflineEconomy.restoreInventoryFromStats();
    }
    else
      await syncPlayerAfterLogin( player as unknown as PlayerDto );
    if ( GlobalSettings.APPSFLYER_ATTRIBUTION_ENABLED )
      void syncUserSourceAfterLogin( player.id );

    if ( GlobalSettings.OFFERS_ENABLED )
    {
      await OfferService.refresh();
      await OfferService.restorePurchases();
    }

    console.log( `[Login] Logged in as ${request.provider}, player id ${player.id}` );
    return true;
  }
  catch ( e )
  {
    console.warn( '[Login] Failed, continuing offline', e );
    return false;
  }
}
